import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { fetchLaydownData, fetchScopeDropdownData, fetchScopeLaydownWiseAtom, fetchScopeListData, setCurrentLaydownAtom } from "../../../atoms/logistics/LogisticsManagementAtom";
import LaydownList from "../../setup/LaydownAndScope/LaydownComponent";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LaydownSvg from "../../setup/LaydownAndScope/laydownSVG";
import settingsAtom, { fetchBlocks } from "../../../atoms/settings/SettingsAtom";
import { fetchMapRender } from "../../../atoms/Dashboard/DashboardAtom";
import ScopeComponent from "../../setup/LaydownAndScope/ScopeManager";
import { useNavigate, useLocation  } from 'react-router-dom';
import { isReadOnly } from "../../../utils/permissionUtils";
import { Add as AddIcon } from "@mui/icons-material";


const LaydownAndScope = (props) => {
    const { value, laydownList, data, loader, setLoader, scopeLaydownProgress, currentLaydown, setCurrentLaydown, setEditScope } = props
    const { svgMap, blocks } = data
    const [settingsData] = useAtom(settingsAtom);
    const { projectList } = settingsData;
    const project_id = localStorage.getItem("project_id");
    const project_name = projectList.find((item) => item.id === parseInt(project_id ?? "0"));
    const navigate = useNavigate();
    const location = useLocation();

    const [visible, setVisible] = useState(true);
    const [, setScopeLaydownWise] = useAtom(fetchScopeLaydownWiseAtom);
    const [, setBlocks] = useAtom(fetchBlocks);
    const [, setMap] = useAtom(fetchMapRender);
    const [, setLaydown] = useAtom(fetchLaydownData);
    const [addLayoutModal, setAddLayoutModal] = useState({ boolean: false, data: { name: "", x: null, y: null } });
    const [laydown, setLaydowns] = useState({ boolean: false, enableAdd: false });
    const [editingItem, setEditingItem] = useState(null);
    const [, setDropdownVaue] = useAtom(fetchScopeDropdownData);
    const { scopeDropdownData, scopeListData } = value;
    const [, setList] = useAtom(fetchScopeListData);
    const [scopeLoader, setScopeLoader] = useState(false);
    const [active, setActive] = useState("");
    const [, setCurrentLaydowns] = useAtom(setCurrentLaydownAtom);
    const path = useLocation();

    useEffect(() => {
        if (currentLaydown?.id) {
            const basePath = `/dashboard/logistics-management/scope/${currentLaydown.id}`;
            const newPath = active === "Scope view" ? `${basePath}/scope-list` : `${basePath}/laydown-list`;

            if (location.pathname !== newPath) {
                navigate(newPath, { replace: true });
            }
        }
    }, [active, currentLaydown?.id, navigate, location.pathname]);

    const handleEditItem = (scope) => {
        setEditScope(scope);
    };

    const handleAddLaydown = () => {
        setLaydowns(prev => ({ ...prev, enableAdd: !laydown.enableAdd }));
    };

    const handleLaydownSelect = (laydown) => {
        setCurrentLaydown(laydown);
        setCurrentLaydowns(laydown);
    };

    const fetchScopeData = async () => {
        try {
            await setScopeLaydownWise();
        } catch (error) {
            console.error("Something went wrong with scope");
        }
    };

    useEffect(() => {
        fetchScopeData();
        if(!laydownList.length) {
            setActive("Laydown view")
        } else if(!active) {
            if(path?.pathname?.split("/")?.[5] === "laydown-list") {
                setActive("Laydown view")
            } else {
                setActive("Scope view")
            }
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoader(true);
            try {
                await Promise.all([
                    setBlocks(''),
                    setMap(''),
                    setLaydown()
                ]);
            } catch (err) {
                console.error(err);
            } finally {
                setLoader(false);
            }
        };
        fetchInitialData();
         // eslint-disable-next-line
    }, [setBlocks, setMap, setLaydown]);

    const fetchScopesData = async (id) => {
        setScopeLoader(true);
        if (id) {
            try {
                await setList(id);
            } catch (err) {
                console.error(err);
            } finally {
                setScopeLoader(false);
            }
        }
    };

    useEffect(() => {
        if (currentLaydown?.id) {
            fetchScopesData(currentLaydown?.id);
        } else if (laydownList?.[0]?.id) {
            fetchScopesData(laydownList?.[0]?.id);
        }
        // eslint-disable-next-line
    }, [currentLaydown, laydownList]);


    return (
        <Box py={1} sx={{ position: "relative" }}>
            {loader ? (
                <Box sx={{ width: "100%", height: "calc(100vh - 230px)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
                    <Box sx={{ width: visible ? "70%" : "100%", transition: "width 0.3s ease", height: "calc(100vh - 240px)" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 2 }}>
                            <Typography fontSize={24} fontWeight={600}>
                                {active === "Laydown view" ? "Laydown And Scope" : "Scope List"}
                            </Typography>
                            {active !== "Laydown view" && (
                                <Box mr={4}>
                                    <Button variant="contained" onClick={() => navigate("/setup/laydown-and-scope/scope-allocated-per-laydown")}>
                                        View all allocated scope
                                    </Button>
                                </Box>
                            )}
                             {active === "Laydown view" && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2,  mr:4 }}>
                                <Button variant="contained" onClick={handleAddLaydown} color="primary" startIcon={<AddIcon />} disabled={isReadOnly()}>
                                    Add New Laydown
                                </Button>
                                </Box>
                            )}
                        </Box>
                        {(active === "Laydown view") ? (
                            <LaydownSvg
                                currentProject={project_name}
                                scopeLaydownProgress={scopeLaydownProgress}
                                currentLaydown={currentLaydown}
                                setCurrentLaydown={setCurrentLaydown}
                                laydownList={laydownList}
                                svgMap={svgMap}
                                blocks={blocks}
                                addLayoutModal={addLayoutModal}
                                setAddLayoutModal={setAddLayoutModal}
                                laydown={laydown}
                                setLaydown={setLaydowns}
                            />
                        ) : (
                            <ScopeComponent
                                data={scopeDropdownData}
                                scopeList={scopeListData}
                                currentLaydown={currentLaydown}
                                loader={scopeLoader}
                                setDropdownVaue={setDropdownVaue}
                                fetchScopesData={fetchScopesData}
                                laydownList={laydownList}
                            />
                        )}
                    </Box>

                    {visible && (
                        <Box
                            sx={{
                                width: "30%",
                                height: "calc(100vh - 230px)",
                                borderLeft: "1px solid #ccc",
                                paddingLeft: 2,
                                transition: "width 0.3s ease",
                            }}
                        >
                            <LaydownList
                                currentLaydown={currentLaydown}
                                setCurrentLaydown={handleLaydownSelect}
                                setCurrentLaydowns={setCurrentLaydowns}
                                laydownList={laydownList}
                                handleAddLaydown={handleAddLaydown}
                                laydown={laydown}
                                onEditItem={handleEditItem}
                                editingItem={editingItem}
                                setEditingItem={setEditingItem}
                                active={active}
                                setActive={setActive}
                                setLaydown={setLaydown}
                                project_id={project_id}
                            />
                        </Box>
                    )}

                    <Box
                        onClick={() => setVisible(!visible)}
                        sx={{
                            height: "24px",
                            border: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "white",
                            zIndex: 10,
                            position: "absolute",
                            top: 10,
                            right: visible ? "30%" : 0,
                            width: "24px",
                            cursor: "pointer",
                            transition: "right 0.3s ease",
                        }}
                    >
                        {visible ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default LaydownAndScope;
